import React, {
  BaseSyntheticEvent,
  useImperativeHandle,
  useState,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import {
  IonModal,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
  IonPopover,
  IonText,
  IonToolbar,
  IonButtons,
  IonContent,
} from '@ionic/react'
import './NumberPad.component.css'
import { backspaceOutline, closeCircle } from 'ionicons/icons'
import { addKeyboardSpacer, removeKeyboardSpacer, isTablet, isPlatformWeb } from '../../utilities'
import { NumericInputService } from '../numericInput/numericInput.service'
import { defaultNumberOfItemsPerRow, digitKeys } from './NumberPad.types'
import NumberPadItem from './numberPadItem'
import { NumberPadConstants } from './numberPad.constants'
import NumberPadItemRows from './numberPadItemRows'
import { getStyleForPHI, IPHIComponent } from '../phi/phi.component'
import ValidationService from '../../features/validation/services/validation.services'
import Toggle from '../toggle/Toggle.component'
import { useSelector, useDispatch } from 'react-redux'
import { getRespiratorySelector } from '../../features/initialAssessments/respiratory/selectors'
import {
  saveRespiratory,
  setFIO2AirTempUnit,
} from '../../features/initialAssessments/respiratory/store/respiratory.actions'
import { useTranslation } from 'react-i18next'
import PNNVBar from '../pnnv/PNNVBar.component'
import { IOptions, IPNNVOption } from '../../common/common.types'
import {
  resetPNNVState,
  setNumberPadAndCalendarCurrentPNNVValue,
  setNumberPadAndCalendarOnPNNVChangeHandler,
  setNumberPadAndCalendarPNNVLabel,
  setNumberPadAndCalendarPNNVListHeader,
  setNumberPadAndCalendarPNNVOptions,
  preventResetPNNVState,
  setNumberPadAndCalendarPNNVNemsis,
  setPNNVStateValues,
  setFreeTextFieldPNNVIsOpen,
  setFreeTextFieldNonPNNVDataReset,
} from '../pnnv/store/pnnv.actions'
import { getPNNVStateSelector } from '../pnnv/selectors'
import PNNVIcon from '../pnnv/PNNVIcon.component'
import Logger from '../../services/logger.service'

export interface INumberPadProps extends IPHIComponent {
  disabled?: boolean
  elementId?: string
  dataTestId: string
  className?: string
  values: string[]
  label?: string
  placeholder?: string
  validCharacters?: string[]
  onNumPadClick: (numbers: string[], prevValue?: string) => void
  onFocus?: (e: any) => void
  onNumberPadDismiss?: () => void
  onBlur?: () => void
  itemsPerRow?: number
  action?: any
  actionKey?: any
  numberPadExtendedLayout?: boolean
  ref?: any
  doneLabel?: string
  fieldHeader?: string
  toggleEnable?: boolean
  toggleDataTestId?: string
  pnnvOptions?: IOptions
  pnnvValue?: IPNNVOption | null | undefined
  pnnvAction?: (...args: any[]) => any
  pnnvLabel?: string
  pnnvListHeader?: string
  nemsis?: string
  useBothNormalAndPertinent?: boolean
  overridePicklistWithNumberPad?: boolean
  type?: string
  onKeyPress?: (e: React.KeyboardEvent | KeyboardEvent) => void
}

const NumberPad: React.FC<INumberPadProps> = forwardRef((props: INumberPadProps, ref) => {
  const {
    elementId,
    dataTestId,
    className = '',
    values = [],
    label,
    placeholder,
    validCharacters,
    onNumPadClick,
    onNumberPadDismiss,
    onFocus,
    itemsPerRow,
    isPHI,
    action,
    actionKey,
    disabled,
    onBlur = () => () => void 0,
    numberPadExtendedLayout = false,
    doneLabel = 'Done',
    fieldHeader = '',
    toggleEnable = false,
    toggleDataTestId = '',
    pnnvOptions = [],
    pnnvValue = undefined,
    pnnvAction = () => () => void 0,
    pnnvLabel = '',
    pnnvListHeader = '',
    nemsis = 'nemsisCode',
    useBothNormalAndPertinent = false,
    overridePicklistWithNumberPad = true,
    type,
    onKeyPress,
  } = props

  const onKeyPressRef = useRef(onKeyPress)
  onKeyPressRef.current = onKeyPress

  //todo: use redux instead of useState
  const [popoverState, setShowPopover] = useState({ showPopover: false, event: undefined })
  const [toggleModal, setModalVisibility] = useState(false)
  const [blinkingHidden, hideBlinker] = useState(true)
  const [popupVisible, setPopupVisible] = useState<boolean>(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const respState = useSelector(getRespiratorySelector)
  const pnnvState = useSelector(getPNNVStateSelector)
  const numberPadAndCalendarState = pnnvState.numberPadAndCalendar
  const { fio2AirTempUnit } = respState
  const [tempUnitToggle, setTempUnitToggle] = useState(false)
  const [localValues, setLocalValues] = useState<string[]>([...values])

  const itemRef = useRef<HTMLIonItemElement>(null)

  useEffect(() => {
    setLocalValues([...values])
  }, [values])

  useEffect(() => {
    return () => removeKeyDownEventListener()
  }, [])

  let defaultNumberPadKeys = digitKeys
  let allDigitsAndSpecialCharacters: string[] = []
  const specialChars: NumberPadItem[] = []

  defaultNumberPadKeys = defaultNumberPadKeys.concat(NumberPadConstants.BACKSPACE)
  allDigitsAndSpecialCharacters = defaultNumberPadKeys

  let actualItemsPerRow = defaultNumberOfItemsPerRow

  const shouldShowPNNVPWAControl =
    isPlatformWeb() &&
    typeof pnnvAction !== 'undefined' &&
    pnnvOptions.length > 0 &&
    pnnvValue === null &&
    !placeholder &&
    !numberPadExtendedLayout

  const setPNNVData = () => {
    dispatch(setNumberPadAndCalendarPNNVOptions(pnnvOptions))
    dispatch(setNumberPadAndCalendarCurrentPNNVValue(pnnvValue))
    dispatch(setNumberPadAndCalendarOnPNNVChangeHandler(pnnvAction))
    dispatch(setNumberPadAndCalendarPNNVLabel(pnnvLabel))
    dispatch(setNumberPadAndCalendarPNNVNemsis(nemsis))
    dispatch(setNumberPadAndCalendarPNNVListHeader(pnnvListHeader))
    const preventResetIdentifier = [8801029]
    dispatch(preventResetPNNVState(preventResetIdentifier))
  }

  if (itemsPerRow) {
    actualItemsPerRow = itemsPerRow
  }

  const numberPadItemRows = {
    rows: new NumberPadItemRows(defaultNumberPadKeys, actualItemsPerRow),
  }

  if (validCharacters?.length) {
    validCharacters.forEach((c) => {
      specialChars.push(new NumberPadItem(c, 'zoll-grey', 'full', 'special-character-button'))
    })
    allDigitsAndSpecialCharacters = defaultNumberPadKeys.concat(validCharacters)
  }

  const numericInputService = new NumericInputService(allDigitsAndSpecialCharacters)

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    const { key } = e
    if (key === 'Enter' && onKeyPressRef.current) {
      e.preventDefault()
      onKeyPressRef.current(e)
      return
    }
    saveNumber(e, key)
  }, [])

  const addKeyDownEventListener = () => {
    document.addEventListener('keydown', onKeyDown)
  }

  const removeKeyDownEventListener = () => {
    document.removeEventListener('keydown', onKeyDown)
  }

  const preventPageScroll = (on: boolean) => {
    const content: HTMLIonContentElement | null = document.querySelector(':not(.ion-page-hidden) > ion-content')
    if (on) {
      content?.classList.add('no-scroll')
    } else {
      content?.classList.remove('no-scroll')
    }
  }

  useEffect(() => {
    return () => {
      removeKeyDownEventListener()
      removeKeyboardSpacer()
      preventPageScroll(false)
      Logger.info(`NumberPad unmount cleanup testId=${dataTestId}`)
    }
  }, [])

  const saveNumber = (e: BaseSyntheticEvent | KeyboardEvent, key: string) => {
    if (!['Tab'].includes(key)) {
      e.preventDefault()
    }
    if (key === NumberPadConstants.BACKSPACE) {
      deleteLastNumber(e)
    } else if (!numericInputService.isValidKeyInput(key)) {
      return
    } else {
      setLocalValues((localValues) => {
        const updatedValues = [...localValues, key]
        onNumPadClick(updatedValues, localValues.join(''))
        return updatedValues
      })
    }
  }

  const deleteLastNumber = (e: BaseSyntheticEvent | KeyboardEvent) => {
    setLocalValues((localValues) => {
      const updated = localValues.slice(0, -1)
      onNumPadClick(updated, localValues.join(''))

      if (updated.length === 0) ValidationService.clearValidation(action)

      return updated
    })
  }

  const handleNumberPadPWA = (e: React.MouseEvent<HTMLIonItemElement>) => {
    const eventTarget = e?.target as HTMLElement
    if (eventTarget?.tagName === 'ION-ICON' || eventTarget?.tagName === 'ION-BUTTON') return
    if (pnnvValue?.name) {
      hideBlinker(true)
      dispatch(setFreeTextFieldPNNVIsOpen(true))
      dispatch(
        setPNNVStateValues({
          customEvent: e,
          showPWAPNNVBar: false,
          pnnvOptions,
          pnnvValue,
          pnnvAction,
          pnnvLabel,
          pnnvListHeader: pnnvListHeader || label,
          nemsis,
          isOpen: true,
        })
      )
      if (!useBothNormalAndPertinent) {
        dispatch(setFreeTextFieldNonPNNVDataReset(() => onNumPadClick([])))
      }
    } else {
      hideBlinker(false)
      addKeyDownEventListener()
      itemRef.current?.focus()
    }
  }

  const showNumberPad = (e: any) => {
    if (e?.target.tagName === 'ION-ICON') return

    if (pnnvOptions.length) {
      setPNNVData()
    }

    hideBlinker(false)

    if (elementId) {
      addKeyboardSpacer(elementId)
    }

    if (onFocus) {
      onFocus(e)
    }

    if (pnnvValue?.name && overridePicklistWithNumberPad) {
      removeKeyDownEventListener()
      setPopupVisible(true)
    } else {
      addKeyDownEventListener()
      setPopupVisible(false)
    }
    if (isTablet()) {
      e?.persist()
      setShowPopover({ showPopover: true, event: e })
    } else {
      preventPageScroll(true)
      setModalVisibility(true)
    }
  }

  useImperativeHandle(ref, () => ({
    showNumberPad,
  }))

  const popoverOnDidDismiss = () => {
    hideBlinker(true)
    removeKeyDownEventListener()
    removeKeyboardSpacer()
    setShowPopover({ showPopover: false, event: undefined })
    if (onNumberPadDismiss) {
      onNumberPadDismiss()
    }
    onBlur()
    dispatch(resetPNNVState())
  }

  const modalOnDidDismiss = () => {
    hideBlinker(true)
    removeKeyDownEventListener()
    removeKeyboardSpacer()
    preventPageScroll(false)
    setModalVisibility(false)
    if (onNumberPadDismiss) {
      onNumberPadDismiss()
    }
    onBlur()
    dispatch(resetPNNVState())
  }

  const onDelete = (e: BaseSyntheticEvent | KeyboardEvent) => {
    e.preventDefault()
    setLocalValues([])
    onBlur()
    onNumPadClick([])
    dispatch(resetPNNVState())
    if (pnnvValue) {
      pnnvAction(null)
      dispatch(setNumberPadAndCalendarCurrentPNNVValue(null))
    }
    ValidationService.clearValidation(action, actionKey)
  }

  const onToggleChange = () => {
    if (fio2AirTempUnit || fio2AirTempUnit === 'f') {
      setTempUnitToggle(false)
      dispatch(setFIO2AirTempUnit('c'))
    }
    if (!fio2AirTempUnit || fio2AirTempUnit === 'c') {
      setTempUnitToggle(true)
      dispatch(setFIO2AirTempUnit('f'))
    }
    dispatch(saveRespiratory())
  }

  const onFocusHandler = () => {
    if (isPlatformWeb() && !pnnvValue?.name && hideBlinker) {
      hideBlinker(false)
      addKeyDownEventListener()
    }
  }

  const onBlurHandler = () => {
    hideBlinker(true)
    removeKeyDownEventListener()
    if (onNumberPadDismiss) {
      onNumberPadDismiss()
    }
    onBlur()
  }

  const renderGridItem = (item: NumberPadItem) => {
    switch (item.char) {
      case NumberPadConstants.EMPTY:
        return <></>
      case NumberPadConstants.BACKSPACE:
        return (
          <div className='backspace-icon-wrapper' onClick={(e) => saveNumber(e, item.dataTestId)}>
            <IonIcon data-testid={item.dataTestId} className='backspace-icon' color='dark' icon={backspaceOutline} />
          </div>
        )

      default:
        return (
          <IonButton
            color={item.color}
            data-testid={item.dataTestId}
            expand={item.expand}
            className={item.className}
            id='numberpad-item-button'
            onClick={(e) => saveNumber(e, item.char)}>
            {item.char}
          </IonButton>
        )
    }
  }

  const hasValue = () => {
    return localValues.length > 0
  }

  const maskPasswordValue = (values: string[]): string => {
    return '*'.repeat(values.length)
  }

  const getValues = () => {
    if (useBothNormalAndPertinent && pnnvValue?.name) {
      return (
        <>
          <span className='tilda'>~</span> {localValues?.join('')}
        </>
      )
    } else if (!useBothNormalAndPertinent && pnnvValue?.name) {
      return pnnvValue.name
    }
    if (type === 'password') {
      return maskPasswordValue(localValues)
    }
    return localValues
  }

  const getItemCustomClassNames = () => {
    if (!blinkingHidden && isPlatformWeb()) return 'number-pad-focused'
    return ''
  }

  const pwaPNNVIcon = (
    <PNNVIcon
      tabIndex={-1}
      pnnvOptions={pnnvOptions}
      pnnvLabel={pnnvLabel}
      pnnvValue={pnnvValue}
      pnnvListHeader={pnnvListHeader || label}
      pnnvAction={pnnvAction}
      onButtonClick={() => {
        if (pnnvOptions?.length && !useBothNormalAndPertinent && pnnvValue?.name) {
          dispatch(setFreeTextFieldPNNVIsOpen(true))
          dispatch(setFreeTextFieldNonPNNVDataReset(() => onNumPadClick([])))
        } else {
          dispatch(resetPNNVState())
        }
      }}
      className='pnnv-icon-pwa-numberpad'
      dataTestId={dataTestId + '-pnnvIcon'}
      nemsis={nemsis}
    />
  )

  const NumberField = (
    <IonItem
      ref={itemRef}
      button
      detail={false}
      disabled={disabled}
      id={elementId}
      data-testid={dataTestId}
      lines={!numberPadExtendedLayout ? 'none' : undefined}
      className={`item-with-number-pad ${className} ion-no-border ${getItemCustomClassNames()}`}
      onClick={(e) => {
        if (!isPlatformWeb()) showNumberPad(e)
        else handleNumberPadPWA(e)
      }}
      onFocus={onFocusHandler}
      onBlur={onBlurHandler}>
      {placeholder && (
        <IonLabel
          className={
            hasValue() || (!useBothNormalAndPertinent && pnnvValue?.name) ? 'ion-hide' : 'number-pad-placeholder'
          }>
          {placeholder}
        </IonLabel>
      )}
      {!placeholder && !numberPadExtendedLayout && !shouldShowPNNVPWAControl && (
        <IonLabel
          position={hasValue() || pnnvValue?.name ? 'floating' : undefined}
          color='medium'
          className='number-pad-label'>
          {label}
        </IonLabel>
      )}
      {!placeholder && !numberPadExtendedLayout && shouldShowPNNVPWAControl && (
        <>
          <IonRow className={`gap-x-3 ${hasValue() ? 'pb-16' : ''}`}>
            <IonLabel color='medium' className='number-pad-pwa-label-floating'>
              {label}
            </IonLabel>
            {pnnvValue === null && pwaPNNVIcon}
          </IonRow>
        </>
      )}
      <span className={hasValue() || pnnvValue?.name ? 'number-pad-span-value w-100' : ''}>
        <IonText
          data-private={isPHI}
          style={getStyleForPHI(isPHI)}
          color='primary-alt'
          data-testid={`${dataTestId}Value`}
          className='number-pad-value'>
          {getValues()}
        </IonText>
        <span
          className={`blinker 
          ${blinkingHidden ? 'ion-hide' : ''}
          ${shouldShowPNNVPWAControl && localValues.length === 0 ? 'p-absolute left-0' : ''}`}>
          |
        </span>
      </span>
      <IonIcon
        className={(hasValue() || pnnvValue) && !disabled ? 'number-pad-delete-icon' : 'ion-hide'}
        icon={closeCircle}
        onClick={onDelete}
        size='small'
        slot='end'
      />
    </IonItem>
  )

  const ExtendedNumberField = () => {
    return (
      <IonContent className='extended-number-field-content'>
        <IonRow>
          <IonCol className='ion-no-padding ion-no-border'>{NumberField}</IonCol>
          <IonCol className='ion-no-padding'>
            <IonItem lines='none' className='item-with-number-pad ion-no-border'>
              {!toggleEnable && (
                <IonLabel color='medium' className='number-pad-label  ion-no-border'>
                  {label}
                </IonLabel>
              )}
              {toggleEnable && (
                <IonLabel color='medium' className='number-pad-label  ion-no-border'>
                  degrees
                </IonLabel>
              )}
              {toggleEnable && (
                <Toggle
                  dataTestId={toggleDataTestId}
                  rightLabel={t('respiratory.TempFahrenheit')}
                  leftLabel={t('respiratory.TempCelcius')}
                  isAirTemp={toggleEnable}
                  onChangeAction={onToggleChange}
                  isChecked={tempUnitToggle}
                />
              )}
            </IonItem>
          </IonCol>
        </IonRow>
      </IonContent>
    )
  }

  const grid = (actionOnDone: () => void) => (
    <>
      {numberPadExtendedLayout && (
        <>
          <IonToolbar color='light'>{fieldHeader}</IonToolbar>
          <ExtendedNumberField />
          <hr className='ion-no-margin' />
          {pnnvOptions.length ? (
            <PNNVBar
              nonPNNVDataReset={() => {
                if (!useBothNormalAndPertinent) onNumPadClick([])
              }}
              actionOnDone={actionOnDone}
              showSingleSelectPopup={popupVisible}
              actionOnCannotComplete={removeKeyDownEventListener}
            />
          ) : null}
          <IonToolbar color='light'>
            <IonButtons slot='end'>
              <IonButton
                color='primary'
                data-testid='doneButton'
                onClick={(e) => {
                  e.stopPropagation()
                  modalOnDidDismiss()
                  popoverOnDidDismiss()
                }}>
                {doneLabel}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </>
      )}

      {!numberPadExtendedLayout && pnnvOptions.length ? (
        <PNNVBar
          nonPNNVDataReset={() => {
            if (!useBothNormalAndPertinent) onNumPadClick([])
          }}
          actionOnDone={actionOnDone}
          showSingleSelectPopup={popupVisible}
          actionOnCannotComplete={removeKeyDownEventListener}
        />
      ) : null}
      {!numberPadAndCalendarState.isOpen && (
        <IonGrid className='grid-container'>
          {numberPadItemRows.rows.items.map((row, rowIndex) => (
            <IonRow key={row.key}>
              {row.items.map((col) =>
                !specialChars.length ? (
                  <IonCol key={col.key} sizeXs='4'>
                    {renderGridItem(col)}
                  </IonCol>
                ) : (
                  <IonCol key={col.key} sizeXs='3.1'>
                    {renderGridItem(col)}
                  </IonCol>
                )
              )}
              {specialChars
                .filter((char, i) => i === rowIndex)
                .map((char) => (
                  <IonCol key={char.key} sizeXs='2.5'>
                    {renderGridItem(char)}
                  </IonCol>
                ))}
            </IonRow>
          ))}
        </IonGrid>
      )}
    </>
  )

  return (
    <>
      <IonPopover
        data-testid='NumberPadPopover'
        className={numberPadExtendedLayout ? 'number-pad-popover extended-popover' : 'number-pad-popover'}
        show-backdrop='false'
        event={popoverState.event}
        isOpen={popoverState.showPopover}
        onDidDismiss={popoverOnDidDismiss}>
        {grid(popoverOnDidDismiss)}
      </IonPopover>

      <IonModal
        data-testid='NumberPadModal'
        showBackdrop={false}
        isOpen={toggleModal}
        onDidDismiss={modalOnDidDismiss}
        className={numberPadExtendedLayout ? 'number-pad-modal extended-pad' : 'number-pad-modal'}>
        {grid(modalOnDidDismiss)}
      </IonModal>
      {!numberPadExtendedLayout && NumberField}
    </>
  )
})

NumberPad.displayName = 'NumberPad'
export default NumberPad
